import React, {useEffect, useState} from 'react';
import dayjs from 'dayjs';
import styled from "styled-components";
import {Alert, InputNumber, message} from "antd";
import {CustomCard} from "../../../../../components/UI/AntCustom/CustomCard";
import {CustomButton} from "../../../../../components/UI/AntCustom/CustomButton";
import {useIntl, defineMessages} from "react-intl";
import {useAppDispatch, useAppSelector} from "../../../../../store/store";
import {
  clearUpdateSubscriptionQuantity,
  updateSubscriptionQuantity,
} from "../../../../../store/reducers/tariffReducer/tariffReducer";
import {ITariffPackage} from "../../../../../store/reducers/tariffReducer/tariffReducerTypes";

const AdjustSeatsMessages = defineMessages({
  title: {
    id: "adjustSeats.title",
    defaultMessage: "Adjust Seats",
  },
  usersLabel: {
    id: "adjustSeats.usersLabel",
    defaultMessage: "Seats (users)",
  },
  aircraftLabel: {
    id: "adjustSeats.aircraftLabel",
    defaultMessage: "Aircraft slots",
  },
  payNow: {
    id: "adjustSeats.payNow",
    defaultMessage: "Update & Pay Now",
  },
  payNowWithAmount: {
    id: "adjustSeats.payNowWithAmount",
    defaultMessage: "Update & Pay Now ({amount})",
  },
  update: {
    id: "adjustSeats.update",
    defaultMessage: "Update",
  },
  downgradeHint: {
    id: "adjustSeats.downgradeHint",
    defaultMessage: "You cannot reduce below {count} {type} — that many are currently in use. The lower price will apply from the next billing cycle.",
  },
  downgradeApplyNextPeriod: {
    id: "adjustSeats.downgradeApplyNextPeriod",
    defaultMessage: "The change will apply from the next billing period.",
  },
  upgradeHint: {
    id: "adjustSeats.upgradeHint",
    defaultMessage: "Upgrades are prorated until the end of the billing period. Payment will be collected immediately. You will pay {monthlyAmount}/month for the additional {items}.",
  },
  upgradeHintItemsSeats: {
    id: "adjustSeats.upgradeHintItemsSeats",
    defaultMessage: "seats",
  },
  upgradeHintItemsAircraft: {
    id: "adjustSeats.upgradeHintItemsAircraft",
    defaultMessage: "aircraft slots",
  },
  upgradeHintItemsBoth: {
    id: "adjustSeats.upgradeHintItemsBoth",
    defaultMessage: "seats and aircraft slots",
  },
  upgradeHintTrial: {
    id: "adjustSeats.upgradeHintTrial",
    defaultMessage: "Upgrade will apply from {date}. No charge now.",
  },
  users: {
    id: "adjustSeats.users",
    defaultMessage: "seats",
  },
  aircraft: {
    id: "adjustSeats.aircraft",
    defaultMessage: "aircraft slots",
  },
  success: {
    id: "adjustSeats.success",
    defaultMessage: "Subscription updated successfully",
  },
});

const SectionContent = styled.div`
  display: flex;
  flex-direction: column;
  row-gap: 16px;
`;

const RowLabel = styled.div`
  display: flex;
  flex-direction: column;
  row-gap: 4px;
  flex: 1;
`;

const Label = styled.label`
  font-size: 14px;
  font-weight: 500;
  color: #333;
`;

const Hint = styled.div`
  font-size: 12px;
  color: #666;
`;

const InputRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
`;

function formatMoney(cents: number, currency: string): string {
  const code = currency?.toUpperCase() || 'USD';
  if (code === 'USD') {
    return `$${(cents / 100).toFixed(2)}`;
  }
  return new Intl.NumberFormat(undefined, { style: 'currency', currency: code }).format(cents / 100);
}

interface IAdjustSeatsSection {
  activePackage: ITariffPackage | null;
  isOperator: boolean;
  pricePerSeatCents?: number;
  pricePerAircraftCents?: number;
  currency?: string;
  discountPercent?: number;
  discountApplies?: boolean;
}

export const AdjustSeatsSection: React.FC<IAdjustSeatsSection> = ({
  activePackage,
  isOperator,
  pricePerSeatCents = 0,
  pricePerAircraftCents = 0,
  currency = 'usd',
  discountPercent,
  discountApplies = false,
}) => {
  const intl = useIntl();
  const dispatch = useAppDispatch();
  const updateState = useAppSelector(state => state.tariff.updateSubscriptionQuantity);

  // billed = actual usage (used for min - can't reduce below what's in use)
  // subscribed = what they pay for (used as default display value)
  const billedUsers = activePackage?.billedUsersCount ?? 0;
  const billedAircraft = activePackage?.billedAircraftCount ?? 0;
  const subscribedUsers = activePackage?.subscribedUsersCount ?? 0;
  const subscribedAircraft = activePackage?.subscribedAircraftCount ?? 0;

  // Display subscribed counts (what they bought); min = billed (can't reduce below usage)
  const [usersQuantity, setUsersQuantity] = useState(subscribedUsers || billedUsers);
  const [aircraftQuantity, setAircraftQuantity] = useState(subscribedAircraft || billedAircraft);

  useEffect(() => {
    setUsersQuantity(subscribedUsers || billedUsers);
    setAircraftQuantity(subscribedAircraft || billedAircraft);
  }, [subscribedUsers, subscribedAircraft, billedUsers, billedAircraft]);

  useEffect(() => {
    if (updateState.isSuccess) {
      dispatch(clearUpdateSubscriptionQuantity());
      message.success(intl.formatMessage(AdjustSeatsMessages.success));
    }
    if (updateState.errorMessage) {
      message.error(updateState.errorMessage);
    }
  }, [updateState.isSuccess, updateState.errorMessage, dispatch, intl]);

  const hasStripeSubscription = Boolean(activePackage?.stripeSubscriptionId);
  const subscriptionStatus = activePackage?.subscriptionStatus;
  // Show when subscription is active (has access): active, trialing, past_due - allow editing seats/aircraft
  const hasAccess = ['active', 'trialing', 'past_due'].includes(subscriptionStatus ?? '');
  const hasQuantityToShow = subscribedUsers > 0 || subscribedAircraft > 0 || billedUsers > 0 || (isOperator && billedAircraft > 0);
  const showAdjustSeats = hasStripeSubscription && hasAccess && hasQuantityToShow;

  const usersChanged = usersQuantity !== subscribedUsers;
  const aircraftChanged = isOperator && aircraftQuantity !== subscribedAircraft;
  const hasChanges = usersChanged || aircraftChanged;

  const isUsersDowngrade = usersQuantity < subscribedUsers;
  const isAircraftDowngrade = aircraftQuantity < subscribedAircraft;
  const canReduceUsers = usersQuantity >= billedUsers;
  const canReduceAircraft = aircraftQuantity >= billedAircraft;

  const usersInvalid = isUsersDowngrade && !canReduceUsers;
  const aircraftInvalid = isAircraftDowngrade && !canReduceAircraft;
  const hasValidationError = usersInvalid || aircraftInvalid;

  // Upgrade = user increased from current subscription (subscribed). Downgrade = decreased from subscribed.
  const isUpgrade = (usersQuantity > subscribedUsers) || (aircraftQuantity > subscribedAircraft);
  const isDowngrade = (usersQuantity < subscribedUsers) || (aircraftQuantity < subscribedAircraft);

  // When on trial with future end: no charge now, upgrade applies from trial end.
  const trialEndAt = activePackage?.trialEndAt;
  const isTrialingWithFutureEnd = trialEndAt && dayjs(trialEndAt).isAfter(dayjs());

  // Extra seats = new seats being added (delta from current subscription, not billed usage)
  const extraUsers = Math.max(0, usersQuantity - subscribedUsers);
  const extraAircraft = isOperator ? Math.max(0, aircraftQuantity - subscribedAircraft) : 0;
  let monthlyCents = extraUsers * (pricePerSeatCents ?? 0) + extraAircraft * (pricePerAircraftCents ?? 0);
  if (discountApplies && discountPercent != null && discountPercent > 0) {
    monthlyCents = Math.round(monthlyCents * (100 - discountPercent) / 100);
  }

  // Calculate prorated amount for upgrade (what will be billed now). When trialing: $0 (no charge now).
  const periodEnd = activePackage?.currentPeriodEnd || activePackage?.tariffEnd;
  const proratedAmountCents = (() => {
    if (!isUpgrade) return 0;
    if (isTrialingWithFutureEnd) return 0; // No charge now; upgrade applies from trial end
    if (!periodEnd) return 0;
    const now = dayjs();
    const periodEndDate = dayjs(periodEnd);
    if (!periodEndDate.isValid() || periodEndDate.isBefore(now)) return 0;

    const periodStart = activePackage?.billingCycleAnchor
      ? dayjs(activePackage.billingCycleAnchor)
      : periodEndDate.subtract(1, 'month');
    const daysRemaining = Math.max(0, periodEndDate.diff(now, 'day'));
    let daysInPeriod = periodStart.isValid() ? Math.max(0, periodEndDate.diff(periodStart, 'day')) : 30;
    // Fallback: if period seems wrong (< 14 days for monthly sub), use standard 30-day month
    if (daysInPeriod < 14) daysInPeriod = 30;
    const prorationFactor = Math.min(1, daysRemaining / daysInPeriod);
    return Math.round(monthlyCents * prorationFactor);
  })();

  const handleUpdate = () => {
    if (!activePackage?.companyTariffPackageId || hasValidationError) return;

    dispatch(updateSubscriptionQuantity({
      companyTariffPackageId: activePackage.companyTariffPackageId,
      usersQuantity,
      aircraftQuantity: isOperator ? aircraftQuantity : 0,
    }));
  };

  if (!showAdjustSeats) {
    return null;
  }

  return (
    <CustomCard width={"max"} style={{ width: '100%' }}>
      <div style={{ fontSize: '16px', fontWeight: 600, marginBottom: '12px' }}>
        {intl.formatMessage(AdjustSeatsMessages.title)}
      </div>

      <SectionContent>
        <InputRow>
          <RowLabel>
            <Label>{intl.formatMessage(AdjustSeatsMessages.usersLabel)}</Label>
            <InputNumber
              min={billedUsers}
              max={999}
              value={usersQuantity}
              onChange={(v) => setUsersQuantity(v ?? billedUsers)}
              style={{ width: 120 }}
            />
            {usersInvalid && (
              <Hint style={{ color: '#ff4d4f' }}>
                {intl.formatMessage(AdjustSeatsMessages.downgradeHint, {
                  count: billedUsers,
                  type: intl.formatMessage(AdjustSeatsMessages.users),
                })}
              </Hint>
            )}
          </RowLabel>

          {isOperator && (
            <RowLabel>
              <Label>{intl.formatMessage(AdjustSeatsMessages.aircraftLabel)}</Label>
              <InputNumber
                min={billedAircraft}
                max={999}
                value={aircraftQuantity}
                onChange={(v) => setAircraftQuantity(v ?? billedAircraft)}
                style={{ width: 120 }}
              />
              {aircraftInvalid && (
                <Hint style={{ color: '#ff4d4f' }}>
                  {intl.formatMessage(AdjustSeatsMessages.downgradeHint, {
                    count: billedAircraft,
                    type: intl.formatMessage(AdjustSeatsMessages.aircraft),
                  })}
                </Hint>
              )}
            </RowLabel>
          )}
        </InputRow>

        {isUpgrade && hasChanges && (
          <Hint>
            {isTrialingWithFutureEnd
              ? intl.formatMessage(AdjustSeatsMessages.upgradeHintTrial, {
                  date: dayjs(trialEndAt).format('DD.MM.YYYY'),
                })
              : intl.formatMessage(AdjustSeatsMessages.upgradeHint, {
                  monthlyAmount: formatMoney(monthlyCents, currency),
                  items: extraUsers > 0 && extraAircraft > 0
                    ? intl.formatMessage(AdjustSeatsMessages.upgradeHintItemsBoth)
                    : extraAircraft > 0
                      ? intl.formatMessage(AdjustSeatsMessages.upgradeHintItemsAircraft)
                      : intl.formatMessage(AdjustSeatsMessages.upgradeHintItemsSeats),
                })}
          </Hint>
        )}

        {isDowngrade && hasChanges && !hasValidationError && (
          <Hint>
            {intl.formatMessage(AdjustSeatsMessages.downgradeApplyNextPeriod)}
          </Hint>
        )}

        {hasValidationError && (
          <Alert
            message={
              usersInvalid && aircraftInvalid
                ? intl.formatMessage(AdjustSeatsMessages.downgradeHint, {
                    count: Math.max(billedUsers, billedAircraft),
                    type: intl.formatMessage(AdjustSeatsMessages.users),
                  })
                : usersInvalid
                  ? intl.formatMessage(AdjustSeatsMessages.downgradeHint, {
                      count: billedUsers,
                      type: intl.formatMessage(AdjustSeatsMessages.users),
                    })
                  : intl.formatMessage(AdjustSeatsMessages.downgradeHint, {
                      count: billedAircraft,
                      type: intl.formatMessage(AdjustSeatsMessages.aircraft),
                    })
            }
            type="warning"
            showIcon
          />
        )}

        {hasChanges && !hasValidationError && (
          <CustomButton
            type="primary"
            onClick={handleUpdate}
            loading={updateState.isLoading}
            disabled={updateState.isLoading}
          >
            {isUpgrade && !isTrialingWithFutureEnd && proratedAmountCents > 0
              ? intl.formatMessage(AdjustSeatsMessages.payNowWithAmount, {
                  amount: formatMoney(proratedAmountCents, currency),
                })
              : intl.formatMessage(AdjustSeatsMessages.update)}
          </CustomButton>
        )}
      </SectionContent>
    </CustomCard>
  );
};
